import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useCall, CALL_STATE } from '../../contexts/CallContext';
import CallMediaManager from './CallMediaManager';
import useRingtone from './useRingtone';
import useActiveSpeaker from './useActiveSpeaker';
import './CallOverlay.css';

const CallOverlay = () => {
  const {
    callState,
    currentCall,
    localStream,
    remoteStreams,
    participants,
    incomingCalls,
    isMuted,
    isCameraOff,
    isMinimized,
    acceptCall,
    rejectCall,
    endCall,
    toggleMic,
    toggleCamera,
    minimize,
    restore,
    dismissIncoming,
  } = useCall() || {};

  const localVideoRef = useRef(null);
  const [callDuration, setCallDuration] = useState(0);
  const callStartTimeRef = useRef(null);

  // Active speaker detection
  const { activeSpeakerId, speakerLevels } = useActiveSpeaker(remoteStreams);

  // Ringtone for incoming/outgoing calls
  useRingtone(callState === CALL_STATE.INCOMING, 'incoming');
  useRingtone(callState === CALL_STATE.OUTGOING, 'outgoing');

  // Attach local stream to video element
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Call timer
  useEffect(() => {
    if (callState === CALL_STATE.IN_CALL && !callStartTimeRef.current) {
      callStartTimeRef.current = Date.now();
    }
    if (callState !== CALL_STATE.IN_CALL) {
      callStartTimeRef.current = null;
      setCallDuration(0);
      return;
    }

    const interval = setInterval(() => {
      if (callStartTimeRef.current) {
        setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [callState]);

  // Format duration as mm:ss or hh:mm:ss
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render if idle
  if (!callState || callState === CALL_STATE.IDLE) {
    return null;
  }

  // Get the first incoming call for display
  const incomingCall = incomingCalls?.[0];

  // Render into a portal so it's always on top
  const overlayContent = (
    <div className={`call-overlay ${isMinimized ? 'minimized' : ''}`}>
      {/* Media manager - handles mediasoup connection */}
      <CallMediaManager />
      {/* Incoming call UI */}
      {callState === CALL_STATE.INCOMING && incomingCall && (
        <div className="call-overlay-incoming">
          <div className="call-overlay-header">
            <span className="call-type-badge">{incomingCall.mode === 'video' ? '🎥 Video' : '📞 Audio'} Call</span>
          </div>
          <div className="call-overlay-caller">
            <div className="caller-avatar">
              {incomingCall.fromUserAvatar ? (
                <img src={incomingCall.fromUserAvatar} alt="" />
              ) : (
                <div className="avatar-placeholder">{incomingCall.fromUserName?.[0] || '?'}</div>
              )}
            </div>
            <div className="caller-info">
              <h3>{incomingCall.fromUserName || 'Unknown'}</h3>
              <p>{incomingCall.threadType === 'direct' ? 'Incoming call' : `${incomingCall.threadName || 'Group'} call`}</p>
            </div>
          </div>
          <div className="call-overlay-actions">
            <button className="call-btn reject" onClick={() => rejectCall(incomingCall.callId)} title="Decline">
              <span>✕</span>
            </button>
            <button className="call-btn accept" onClick={() => acceptCall(incomingCall.callId)} title="Accept">
              <span>✓</span>
            </button>
          </div>
          {/* For group calls, show option to dismiss without rejecting */}
          {incomingCall.isGroupCall && (
            <button 
              className="dismiss-btn" 
              onClick={() => dismissIncoming?.(incomingCall.callId)}
            >
              Dismiss (join later)
            </button>
          )}
        </div>
      )}

      {/* Outgoing call UI */}
      {callState === CALL_STATE.OUTGOING && currentCall && (
        <div className="call-overlay-outgoing">
          <div className="call-overlay-header">
            <span className="call-type-badge">{currentCall.mode === 'video' ? '🎥 Video' : '📞 Audio'} Call</span>
          </div>
          <div className="call-overlay-caller">
            <div className="caller-avatar ringing">
              {currentCall.thread?.avatar ? (
                <img src={currentCall.thread.avatar} alt="" />
              ) : (
                <div className="avatar-placeholder">{currentCall.thread?.name?.[0] || '?'}</div>
              )}
            </div>
            <div className="caller-info">
              <h3>{currentCall.thread?.name || 'Calling...'}</h3>
              <p>Ringing...</p>
            </div>
          </div>
          {/* Local preview */}
          {currentCall.mode === 'video' && localStream && (
            <div className="local-preview-outgoing">
              <video ref={localVideoRef} autoPlay playsInline muted />
            </div>
          )}
          <div className="call-overlay-actions">
            <button className="call-btn reject" onClick={endCall} title="Cancel">
              <span>✕</span>
            </button>
          </div>
        </div>
      )}

      {/* Connecting UI */}
      {callState === CALL_STATE.CONNECTING && (
        <div className="call-overlay-connecting">
          <div className="call-overlay-header">
            <span className="call-type-badge">{currentCall?.mode === 'video' ? '🎥 Video' : '📞 Audio'} Call</span>
          </div>
          <div className="connecting-spinner" />
          <p>Connecting...</p>
          <button className="call-btn reject" onClick={endCall} title="Cancel">
            <span>✕</span>
          </button>
        </div>
      )}

      {/* In-call UI */}
      {callState === CALL_STATE.IN_CALL && currentCall && !isMinimized && (
        <div className="call-overlay-incall">
          <div className="call-overlay-header">
            <div className="call-info">
              <span className="call-type-badge">{currentCall.mode === 'video' ? '🎥' : '📞'}</span>
              <span className="call-name">{currentCall.thread?.name || 'Call'}</span>
              <span className="call-timer">{formatDuration(callDuration)}</span>
            </div>
            <button className="minimize-btn" onClick={minimize} title="Minimize">
              <span>−</span>
            </button>
          </div>

          {/* Video grid */}
          <div className={`video-grid participants-${Math.min(remoteStreams?.size || 0, 4) + 1}`}>
            {/* Remote streams */}
            {Array.from(remoteStreams?.entries() || []).map(([odlerId, stream]) => (
              <RemoteVideo 
                key={odlerId} 
                odlerId={odlerId} 
                stream={stream} 
                participants={participants}
                isActiveSpeaker={activeSpeakerId === odlerId}
                speakerLevel={speakerLevels[odlerId] || 0}
              />
            ))}

            {/* Local video (self) */}
            <div className={`video-tile local ${currentCall.mode === 'audio' ? 'audio-only' : ''}`}>
              {currentCall.mode === 'video' && !isCameraOff ? (
                <video ref={localVideoRef} autoPlay playsInline muted />
              ) : (
                <div className="audio-avatar">
                  <div className="avatar-placeholder">You</div>
                </div>
              )}
              <div className="video-tile-label">You {isMuted && '🔇'}</div>
            </div>
          </div>

          {/* Controls bar */}
          <div className="call-controls">
            <button
              className={`control-btn ${isMuted ? 'off' : ''}`}
              onClick={toggleMic}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? '🔇' : '🎤'}
            </button>
            {currentCall.mode === 'video' && (
              <button
                className={`control-btn ${isCameraOff ? 'off' : ''}`}
                onClick={toggleCamera}
                title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
              >
                {isCameraOff ? '📷' : '🎥'}
              </button>
            )}
            <button className="control-btn end" onClick={endCall} title="End call">
              📞
            </button>
          </div>
        </div>
      )}

      {/* Minimized bar */}
      {callState === CALL_STATE.IN_CALL && isMinimized && (
        <div className="call-overlay-minimized" onClick={restore}>
          <div className="minimized-info">
            <span className="call-type-icon">{currentCall?.mode === 'video' ? '🎥' : '📞'}</span>
            <span className="call-name">{currentCall?.thread?.name || 'Call'}</span>
            <span className="call-timer">{formatDuration(callDuration)}</span>
          </div>
          <div className="minimized-controls">
            <button
              className={`mini-control ${isMuted ? 'off' : ''}`}
              onClick={(e) => { e.stopPropagation(); toggleMic(); }}
            >
              {isMuted ? '🔇' : '🎤'}
            </button>
            <button
              className="mini-control end"
              onClick={(e) => { e.stopPropagation(); endCall(); }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(overlayContent, document.body);
};

// Remote video tile component
const RemoteVideo = ({ odlerId, stream, participants, isActiveSpeaker, speakerLevel }) => {
  const videoRef = useRef(null);
  const participant = participants?.find(p => p.odlerId === odlerId);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const hasVideo = stream?.getVideoTracks?.()?.some(t => t.enabled);

  return (
    <div className={`video-tile remote ${!hasVideo ? 'audio-only' : ''} ${isActiveSpeaker ? 'active-speaker' : ''}`}>
      {/* Active speaker indicator */}
      {isActiveSpeaker && <div className="speaker-indicator" />}
      
      {hasVideo ? (
        <video ref={videoRef} autoPlay playsInline />
      ) : (
        <div className="audio-avatar">
          {participant?.avatar ? (
            <img src={participant.avatar} alt="" />
          ) : (
            <div className="avatar-placeholder">{participant?.name?.[0] || '?'}</div>
          )}
        </div>
      )}
      <div className="video-tile-label">
        {participant?.name || 'Participant'}
        {participant?.isMuted && ' 🔇'}
      </div>
    </div>
  );
};

export default CallOverlay;
